import { AxiosResponse } from 'axios';
import { toolUserDocumentDelete, toolUserDocumentFileDownload, toolUserDocumentList, toolUserDocumentUpdate } from '@/api/toolUserDocument';
import { AppError } from '@/errors/appError';
import { baseListService, isRequestCanceled } from '@/services/baseService';
import {
    IListParams,
    IToolUserDocumentApiListResponse,
    IToolUserDocumentDeleteParams,
    IToolUserDocumentFilters,
    IToolUserDocumentMutationResponse,
    IToolUserDocumentUpdateParams,
} from '@/types';

export const toolUserDocumentService = {
    async list(params: IListParams<IToolUserDocumentFilters>): Promise<AxiosResponse<IToolUserDocumentApiListResponse>> {
        try {
            return await baseListService<IToolUserDocumentApiListResponse>(toolUserDocumentList, params);
        } catch (e) {
            if (isRequestCanceled(e)) throw e;
            console.error('Tool user document API Error:', e);
            throw new AppError(`Unable to load tool user document list, request error`, { cause: e });
        }
    },

    async delete(params: IToolUserDocumentDeleteParams): Promise<AxiosResponse<{ success: boolean }>> {
        try {
            return await toolUserDocumentDelete(params);
        } catch (e) {
            console.error('Tool user document delete API Error:', e);
            throw new AppError(`Unable to delete document, request error`, { cause: e });
        }
    },

    async update(params: IToolUserDocumentUpdateParams): Promise<AxiosResponse<IToolUserDocumentMutationResponse>> {
        try {
            return await toolUserDocumentUpdate(params);
        } catch (e) {
            console.error('Tool user document update API Error:', e);
            throw new AppError(`Unable to update document, request error`, { cause: e });
        }
    },

    async downloadFile(documentId: number, fallbackFilename: string): Promise<void> {
        try {
            const response = await toolUserDocumentFileDownload(documentId);
            let filename = fallbackFilename.replace(/^.*[/\\]/, '') || `document-${documentId}`;
            const contentDisposition = response.headers['content-disposition'];
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (filenameMatch?.[1]) {
                    filename = filenameMatch[1].replace(/['"]/g, '');
                }
            }
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            if (isRequestCanceled(e)) throw e;
            console.error('Tool user document download API Error:', e);
            throw new AppError(`Unable to download document, request error`, { cause: e });
        }
    },
};
